import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import {
  approveDeletion, cancelDeletionRequest, createTelegramLinkCode,
  getDeletionRequests, rejectDeletion, type DeletionRequestRow,
} from '@/api/deletions';

const STATUS_FILTERS = ['pending', 'approved', 'rejected', 'expired', 'failed', 'obsolete'];

function stamp(seconds: number): string {
  return new Date(seconds * 1000).toISOString().replace('T', ' ').slice(0, 16);
}

export function Deletions() {
  const admin = useAuthStore((s) => s.admin);
  const linked = !!admin?.telegram_linked;
  const [rows, setRows] = useState<DeletionRequestRow[]>([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [linkCode, setLinkCode] = useState<string | null>(null);

  async function load(next: string) {
    setLoading(true);
    try {
      setRows(await getDeletionRequests(next));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(status); }, [status]);

  async function decide(id: string, approve: boolean) {
    setBusy(id);
    try {
      if (approve) await approveDeletion(id);
      else await rejectDeletion(id);
      await load(status);
    } finally {
      setBusy(null);
    }
  }

  async function cancel(id: string) {
    setBusy(id);
    try {
      await cancelDeletionRequest(id);
      await load(status);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-xl font-semibold text-content">Requests</h1>
        <div className="flex items-center gap-3">
          {linked && <span className="text-sm text-content-subtle">Telegram linked</span>}
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-surface-muted"
            onClick={async () => setLinkCode((await createTelegramLinkCode()).code)}
          >
            {linked ? 'Re-link Telegram' : 'Link Telegram'}
          </button>
        </div>
      </div>

      {linkCode && (
        <div className="text-sm text-content-muted border border-border rounded-md p-3">
          Send <code className="text-content">/start {linkCode}</code> to the bot in a private
          chat within 15 minutes. The buttons then work for the kinds of requests your account
          is allowed to decide (set per person on the Users page).
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`text-sm px-3 py-1 rounded-md border ${
              status === s ? 'border-primary text-content' : 'border-border text-content-muted'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-content-muted text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-content-muted text-sm">Nothing here.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-content-subtle">
                <th className="py-2 pr-4">Requested</th>
                <th className="py-2 pr-4">By</th>
                <th className="py-2 pr-4">What</th>
                <th className="py-2 pr-4">Section</th>
                <th className="py-2 pr-4">Target</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Decided by</th>
                <th className="py-2 pr-4">Expires</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="py-2 pr-4 whitespace-nowrap">{stamp(r.created_at)}</td>
                  <td className="py-2 pr-4">{r.requested_by_email}</td>
                  <td className="py-2 pr-4">
                    <span
                      className="mr-1"
                      title={r.kind === 'reveal' ? 'Secret reveal' : 'Deletion'}
                    >
                      {r.kind === 'reveal' ? '🔑' : '🗑'}
                    </span>
                    {r.target_type ? (
                      <>
                        <span className="text-content-subtle">{r.target_type}</span>
                        {r.target_name && <span className="ml-1 font-medium">{r.target_name}</span>}
                      </>
                    ) : (
                      <span className="text-content-subtle">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">{r.section ?? '—'}</td>
                  <td className="py-2 pr-4"><code>{r.method} {r.path}</code></td>
                  <td className="py-2 pr-4">
                    {r.status}
                    {r.result_error && (
                      <span className="text-content-subtle"> — {r.result_error}</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    {r.decided_by_email ? (
                      <>
                        <div>{r.decided_by_name || r.decided_by_email}</div>
                        <div className="text-content-subtle text-xs">
                          {r.decided_via}
                          {r.decided_at ? ` · ${stamp(r.decided_at)}` : ''}
                        </div>
                      </>
                    ) : (
                      <span className="text-content-subtle">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{stamp(r.expires_at)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {r.status === 'pending' && r.can_approve && (
                      <button
                        type="button"
                        disabled={busy === r.id}
                        onClick={() => decide(r.id, true)}
                        className="text-sm px-2 py-1 rounded-md border border-border hover:bg-surface-muted mr-2"
                      >
                        Approve
                      </button>
                    )}
                    {r.status === 'pending' && r.can_reject && r.requested_by_admin_id !== admin?.id && (
                      <button
                        type="button"
                        disabled={busy === r.id}
                        onClick={() => decide(r.id, false)}
                        className="text-sm px-2 py-1 rounded-md border border-border hover:bg-surface-muted"
                      >
                        Reject
                      </button>
                    )}
                    {r.status === 'pending' && r.requested_by_admin_id === admin?.id && (
                      <button
                        type="button"
                        disabled={busy === r.id}
                        onClick={() => cancel(r.id)}
                        className="text-sm px-2 py-1 rounded-md border border-border hover:bg-surface-muted"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
